import React from 'react';
import { twJoin } from 'tailwind-merge';

import { type ProfileItemPart, renderProfileItemPart } from '@/components/profile/ProfileItemPart';
import { ProfileSectionHeader } from '@/components/profile/ProfileSectionHeader';
import { ProfileItemMeta } from '@/components/profile/ProfileText';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import type { UserV2 } from '@/services/getUserV2';

type WorkHistoryItemData = NonNullable<UserV2['workHistory']>[number];

interface WorkHistorySectionProps {
  section: {
    title: string;
    data: readonly any[];
  };
  isFirstSection: boolean;
  isOwnProfile?: boolean;
  onEdit?: () => void;
}

/**
 * Decomposes a work history entry into the shared profile item parts: the
 * position title, the organization, and the role description (as bullets when
 * it already reads as a list, otherwise as plain lines).
 */
export function formatWorkHistoryItemContent(
  workHistoryItem: WorkHistoryItemData
): ProfileItemPart[] {
  const { positionTitle, organization, roleDescription } = workHistoryItem;

  const content: ProfileItemPart[] = [];

  if (positionTitle) {
    content.push({ type: 'title', text: positionTitle });
  }

  if (organization) {
    content.push({ type: 'meta', text: organization });
  }

  if (roleDescription) {
    const lines = roleDescription.split('\n');

    const hasExistingBullets = lines.some(
      (line) => line.trim().match(/^[•\-*]\s/) || line.trim().match(/^\d+\.\s/)
    );

    content.push({
      type: hasExistingBullets ? 'bullets' : 'paragraph',
      text: roleDescription,
    });
  }

  return content;
}

/**
 * WorkHistorySection - Profile work history display component
 *
 * Renders a user's work history with structured content formatting.
 * Features include:
 * - Dynamic content parsing with bullet point detection
 * - Structured rendering of position, organization, and descriptions
 * - Conditional edit button for profile owners
 * - Responsive layout with proper spacing
 * - Support for both bulleted and plain text descriptions
 * - Date range display for each work entry
 */
export const WorkHistorySection = ({
  section,
  isFirstSection,
  isOwnProfile,
  onEdit,
}: WorkHistorySectionProps) => {
  return (
    <View>
      <ProfileSectionHeader title={section.title} isOwnProfile={isOwnProfile} onEdit={onEdit} />
      {section.data.map((item: any, index: number) => {
        const parts = item.accoladeParts || [];

        return (
          <VStack
            key={`${item.title || 'work-item'}-${index}`}
            className={twJoin(
              'mx-auto w-full max-w-5xl items-start px-6',
              section.data.length - 1 !== index ? 'mb-4' : ''
            )}>
            {item.title ? (
              <ProfileItemMeta className="mb-1 leading-none">{item.title}</ProfileItemMeta>
            ) : null}

            {parts.map((part: ProfileItemPart, partIndex: number) => (
              <React.Fragment key={partIndex}>
                {renderProfileItemPart({ item: part })}
              </React.Fragment>
            ))}
          </VStack>
        );
      })}
    </View>
  );
};
